import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isTokenExpired } from '@/utils/isTokenExpired'
import { useClient, resetClient } from '@/composables/useClient'
import { jwtDecode } from 'jwt-decode'
import dayjs from 'dayjs'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>()
  const isAuthenticated = computed(() => !!accessToken.value)
  const lastTokenCheck = ref(0)

  const getAccessToken = async (): Promise<string | undefined> => {
    return accessToken.value || undefined
  }

  const setAccessToken = (token: string) => {
    accessToken.value = token
  }

  const removeAccessToken = () => {
    accessToken.value = null
  }

  const refreshToken = async (): Promise<void> => {
    const client = await useClient()
    const { status, body } = await client.auth.refresh()

    if (status === 200) {
      setAccessToken(body.accessToken)
    } else {
      removeAccessToken()
    }
  }

  const logout = async (): Promise<void> => {
    const client = await useClient()
    await client.auth.logout({ body: {} })
    await removeAccessToken()
    resetClient()
  }

  const checkIfTokenNeedRefresh = async (): Promise<void> => {
    const token = accessToken.value

    if (!token || isTokenExpired(token)) {
      console.debug("No access token or it's expired, attempting to refresh from cookie")
      await refreshToken()
    }
  }

  const checkIfTokenExpired = async (): Promise<void> => {
    const token = accessToken.value
    if (!token) return

    const now = dayjs()

    try {
      const decoded = jwtDecode<{ exp: number }>(token)
      const expired = dayjs.unix(decoded.exp)
      const expiredMinusTwoMin = expired.subtract(2, 'minute')

      if (dayjs().isAfter(expiredMinusTwoMin)) {
        console.debug('Access token is expired, refreshing token...')
        await refreshToken()
      }
    } catch {
      console.error('Failed to decode access token')
    }
    lastTokenCheck.value = now.valueOf()
  }

  return {
    isAuthenticated,
    checkIfTokenNeedRefresh,
    checkIfTokenExpired,
    getAccessToken,
    setAccessToken,
    removeAccessToken,
    refreshToken,
    logout,
  }
})
